import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import './rich-text-editor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function RichTextEditor({ value, onChange, placeholder, disabled }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat editor-empty state as empty string so validation works correctly
      onChange(editor.isEmpty ? '' : html);
    },
  });

  // Sync external value changes (e.g. when loading an existing recipe)
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  // Sync disabled state
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <div className={`rich-text-editor${disabled ? ' rich-text-editor--disabled' : ''}`}>
      <div className="rich-text-editor__toolbar" role="toolbar" aria-label="Text formatting">
        <button
          type="button"
          onClick={() => editor?.chain().focus().toggleBold().run()}
          disabled={disabled || !editor?.can().toggleBold()}
          className={`toolbar-btn${editor?.isActive('bold') ? ' toolbar-btn--active' : ''}`}
          aria-label="Bold"
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          disabled={disabled || !editor?.can().toggleItalic()}
          className={`toolbar-btn${editor?.isActive('italic') ? ' toolbar-btn--active' : ''}`}
          aria-label="Italic"
          title="Italic"
        >
          <em>I</em>
        </button>
        <div className="toolbar-divider" />
        <button
          type="button"
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
          className={`toolbar-btn${editor?.isActive('orderedList') ? ' toolbar-btn--active' : ''}`}
          aria-label="Numbered list"
          title="Numbered list"
        >
          1.
        </button>
        <button
          type="button"
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          disabled={disabled}
          className={`toolbar-btn${editor?.isActive('bulletList') ? ' toolbar-btn--active' : ''}`}
          aria-label="Bullet list"
          title="Bullet list"
        >
          •—
        </button>
      </div>
      <div className="rich-text-editor__content-wrapper">
        <EditorContent
          editor={editor}
          className="rich-text-editor__content"
        />
        {editor?.isEmpty && placeholder && (
          <div className="rich-text-editor__placeholder" aria-hidden="true">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

export default RichTextEditor;
